import * as React from "react";

import type {Assignment} from "@khan/assignments-types";
import {useMutation} from "@khan/data-gql";

import {ARCHIVE_ASSIGNMENTS_MUTATION} from "./queries.ts";

export interface ArchiveContentAssignmentsResult {
    isArchiving: boolean;
}

export type ArchiveContentAssignmentsAction = (ids: Array<string>) => void;

interface Props {
    children: (
        archiveAssignmentsAction: ArchiveContentAssignmentsAction,
        data: ArchiveContentAssignmentsResult,
    ) => React.ReactNode;
    onSuccess: (archivedAssignments: ReadonlyArray<Assignment>) => void;
    onError: (error: Error) => void;
}

const ArchiveContentAssignmentsMutation = (props: Props) => {
    const [archiveAssignments, result] = useMutation(
        ARCHIVE_ASSIGNMENTS_MUTATION,
    );

    const archiveAction = React.useCallback(
        async (assignmentIds: Array<string>) => {
            const mutationResult = await archiveAssignments({assignmentIds});

            if (mutationResult.status === "success") {
                const data = mutationResult.data;
                props.onSuccess(
                    // NOTE: `Assignment` contains a number of fields that are not present
                    // in the `archiveContentAssignments` mutation data.  Removing these
                    // fields causes TypeScript to report a lot of type errors.  I'm not
                    // sure how code that uses this data is working without these fields.
                    (data?.archiveAssignments?.assignments ??
                        []) as unknown as ReadonlyArray<Assignment>,
                );
            } else if (mutationResult.status === "error") {
                props.onError(mutationResult.error);
            }
        },
        [archiveAssignments, props],
    );

    return props.children(archiveAction, {
        isArchiving: result?.status === "loading",
    });
};

export default ArchiveContentAssignmentsMutation;
